import React, { useEffect, useState } from 'react';
import './ContextualDataPanel.css';

/**
 * ContextualDataPanel Component
 * 
 * Displays relevant contextual information about the patient and study.
 * Designed for minimal cognitive load with clear visual hierarchy.
 * 
 * Features:
 * - Patient demographic information
 * - Study metadata (modality, date, etc.)
 * - Historical context (if available)
 * - Extensible for future contextual features
 * 
 * @param {Object} props - Component props
 * @param {Object} props.contextualData - Study and patient context data
 * @param {Object} props.imageInfo - Information about the currently displayed image
 * @param {string} props.loadingState - Loading state ('loading', 'success', 'error')
 */
const ContextualDataPanel = ({ contextualData, imageInfo, loadingState }) => {

  const [modality, setModality] = useState(null);
  const [bodyPartExamined, setBodyPartExamined] = useState(null);
  const [viewPosition, setViewPosition] = useState(null);
  const [laterality, setLaterality] = useState(null);

  useEffect(() => {
    if (!contextualData || !Array.isArray(contextualData) || !imageInfo) {
      return;
    }
    console.log("ContextualDataPanel - Received imageInfo: ", imageInfo);

    // Parse the stackId format: "dataItemIndex-seriesNumber"
    const stackIdParts = String(imageInfo.stackId).split('-');
    const dataItemIndex = parseInt(stackIdParts[0], 10);
    const seriesNumber = stackIdParts.slice(1).join('-'); // Handle cases where seriesNumber might contain dashes

    // Get the correct study from contextualData
    const study = contextualData[dataItemIndex];
    if (!study || !study.seriesList) {
      return;
    }

    // Find the series by seriesNumber
    const currentSerie = study.seriesList.find(series => String(series.seriesNumber) === seriesNumber);
    console.log("ContextualDataPanel - Current Serie: ", currentSerie);

    if (!currentSerie || !currentSerie.instanceList) {
      return;
    }

    const currentInstance = currentSerie.instanceList.find(
      instance => instance.instanceNumber === imageInfo.imageIndex + 1
    ) || currentSerie.instanceList[0];
    console.log("ContextualDataPanel - Current Instance: ", currentInstance);

    if (!currentInstance || !currentInstance.tags) {
      return;
    }

    setModality(currentInstance.tags.find(tag => tag.modality)?.modality || 'Unknown');
    setBodyPartExamined(currentInstance.tags.find(tag => tag.bodyPartExamined)?.bodyPartExamined || 'Unknown');
    setViewPosition(currentInstance.tags.find(tag => tag.viewPosition)?.viewPosition || 'Unknown');
    const lateralityValue = currentInstance.tags.find(tag => tag.imageLaterality)?.imageLaterality;
    const lateralityDisplay = lateralityValue === 'L' ? 'Left' :
      lateralityValue === 'R' ? 'Right' :
        lateralityValue || 'Unknown';
    setLaterality(lateralityDisplay);
  }, [contextualData, imageInfo]);

  // Loading state
  if (loadingState === 'loading') {
    return (
      <div className="contextual-panel">
        <div className="panel-header">
          <h4>Contextual Data</h4>
        </div>
        <div className="panel-content loading">
          <div className="loading-spinner"></div>
          <p>Loading contextual data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadingState === 'error') {
    return (
      <div className="contextual-panel">
        <div className="panel-header">
          <h4>Contextual Data</h4>
        </div>
        <div className="panel-content error">
          <p>No contextual data available</p>
        </div>
      </div>
    );
  }

  // No patient selected
  if (loadingState === 'no-patient') {
    return (
      <div className="contextual-panel">
        <div className="panel-header">
          <h4>Contextual Data</h4>
        </div>
        <div className="panel-content empty">
          <p>Please select a patient to view contextual data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contextual-panel">
      <div className="panel-header">
        <h4>Contextual Data</h4>
      </div>

      <div className="panel-content">
        {/* Patient Information */}
        <div className="context-section">
          Patient Data
          <div className="section-label">Patient Name</div>
          <div className="section-value">
            {contextualData?.[0]?.patientName}
          </div>
          <div className="section-label">Patient ID</div>
          <div className="section-value">
            {contextualData?.[0]?.patientId}
          </div>
        </div>

        {/* Study Information */}
        <div className="context-section">
          Study Data

          {modality && modality !== 'Unknown' && (
            <>
              <div className="section-label">Modality</div>
              <div className="section-value">
                {modality}
              </div>
            </>
          )}
          {bodyPartExamined && bodyPartExamined !== 'Unknown' && (
            <>
              <div className="section-label">Body Part</div>
              <div className="section-value">
                {bodyPartExamined}
              </div>
            </>
          )}
          {viewPosition && viewPosition !== 'Unknown' && (
            <>
              <div className="section-label">View Position</div>
              <div className="section-value">
                {viewPosition}
              </div>
            </>
          )}
          {laterality && laterality !== 'Unknown' && (
            <>
              <div className="section-label">Laterality</div>
              <div className="section-value">
                {laterality}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextualDataPanel;
